import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { fetchGelenCagrilar, fetchGidenCagrilar } from '../services/santralYonetimService';
import TableListFooter from './TableListFooter';
import { PAGE_DEFAULT_LIMIT } from '../constants/pagination';
import { parseApiDate } from '../utils/security';
import { useConfirmDialog } from '../contexts/ConfirmContext';
import SesKaydiPlayerModal from './SesKaydiPlayerModal';
import GelenCagriDetayModal from './GelenCagriDetayModal';
import GelenCagriFilterModal from './GelenCagriFilterModal';
import './SesKaydiPlayerModal.css';
import './GelenCagriDetayModal.css';
import './GelenCagriFilterModal.css';

const bugunStr = () => {
  const d = new Date();
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

const yediGunOnceStr = () => {
  const d = new Date();
  d.setDate(d.getDate() - 7);
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

export const varsayilanCagriFiltre = (telefon = '') => ({
  baslangic: yediGunOnceStr(),
  bitis: bugunStr(),
  mesai: '',
  durum: '',
  sureMin: '',
  sureMax: '',
  telefon: telefon || '',
});

const filtreAktifMi = (f, varsayilan) => (
  f.mesai !== ''
  || f.durum !== ''
  || f.sureMin !== ''
  || f.sureMax !== ''
  || (f.telefon || '').trim() !== (varsayilan.telefon || '').trim()
  || f.baslangic !== varsayilan.baslangic
  || f.bitis !== varsayilan.bitis
);

const getErrorMessage = (error, fallback) => {
  if (!error) return fallback;
  const msg = error?.message || error?.data?.mesaj || error?.data?.message || fallback;
  return msg === 'Failed to fetch' ? 'Sunucuya bağlanılamadı' : msg;
};

const formatTarih = (val) => {
  const d = parseApiDate(val);
  if (!d || Number.isNaN(d.getTime())) return val || '—';
  return d.toLocaleString('tr-TR', { dateStyle: 'short', timeStyle: 'short' });
};

const cevaplandiYon = (tip) => (tip === 'giden' ? 0 : 1);

/**
 * Netgsm CDR çağrı listesi (gelen veya giden).
 * @param {object} props
 * @param {'gelen'|'giden'} props.tip
 * @param {string} [props.telefon] - Sabit telefon filtresi (müşteri sayfası)
 * @param {boolean} [props.hideMusteriKolonu]
 * @param {boolean} [props.hideTelefonFiltresi]
 * @param {boolean} [props.aktif=true] - false ise fetch yapılmaz
 */
const MusteriCagriListesi = ({
  tip = 'gelen',
  telefon: sabitTelefon = '',
  hideMusteriKolonu = false,
  hideTelefonFiltresi = false,
  aktif = true,
}) => {
  const { confirm } = useConfirmDialog();
  const varsayilanRef = useMemo(
    () => varsayilanCagriFiltre(hideTelefonFiltresi ? sabitTelefon : ''),
    [hideTelefonFiltresi, sabitTelefon]
  );

  const [list, setList] = useState([]);
  const [sayfa, setSayfa] = useState(1);
  const [toplam, setToplam] = useState(0);
  const [toplamSayfa, setToplamSayfa] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [filtre, setFiltre] = useState(varsayilanRef);
  const [filtreModalAcik, setFiltreModalAcik] = useState(false);
  const [sesKaydiModal, setSesKaydiModal] = useState(null);
  const [detayModal, setDetayModal] = useState(null);
  const limit = PAGE_DEFAULT_LIMIT;

  useEffect(() => {
    if (hideTelefonFiltresi && sabitTelefon) {
      setFiltre((prev) => ({ ...prev, telefon: sabitTelefon }));
    }
  }, [hideTelefonFiltresi, sabitTelefon]);

  const loadList = useCallback(async () => {
    if (!aktif) return;
    setLoading(true);
    setError('');
    try {
      const telefonParam = hideTelefonFiltresi
        ? (sabitTelefon || '').trim()
        : (filtre.telefon?.trim() || undefined);

      const params = {
        sayfa,
        limit,
        baslangic: filtre.baslangic ? `${filtre.baslangic}T00:00:00` : undefined,
        bitis: filtre.bitis ? `${filtre.bitis}T23:59:59` : undefined,
        mesai: filtre.mesai || undefined,
        durum: filtre.durum || undefined,
        sureMin: filtre.sureMin !== '' ? filtre.sureMin : undefined,
        sureMax: filtre.sureMax !== '' ? filtre.sureMax : undefined,
        telefon: telefonParam || undefined,
      };

      const fetchFn = tip === 'giden' ? fetchGidenCagrilar : fetchGelenCagrilar;
      const res = await fetchFn(params);

      if (res?.basarili && res?.veri) {
        setList(res.veri.cagrilar || []);
        setToplam(res.veri.toplam ?? 0);
        setToplamSayfa(res.veri.toplamSayfa ?? 1);
        if (res.veri.sayfa && res.veri.sayfa !== sayfa) {
          setSayfa(res.veri.sayfa);
        }
      } else {
        setList([]);
        setToplam(0);
        setToplamSayfa(1);
        setError(res?.mesaj || 'Çağrılar yüklenemedi');
      }
    } catch (err) {
      setError(getErrorMessage(err, 'Çağrılar yüklenemedi'));
      setList([]);
      setToplam(0);
      setToplamSayfa(1);
    } finally {
      setLoading(false);
    }
  }, [aktif, tip, sayfa, limit, filtre, hideTelefonFiltresi, sabitTelefon]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  const filtreAktif = filtreAktifMi(filtre, varsayilanRef);

  const handleFiltreUygula = (yeniFiltre) => {
    setFiltre(yeniFiltre);
    setSayfa(1);
  };

  const handleFiltreTemizle = async () => {
    const onay = await confirm({
      title: 'Filtreleri temizle',
      message: 'Tüm filtreler varsayılan değerlere dönecek. Devam etmek istiyor musunuz?',
      variant: 'warning',
      confirmLabel: 'Evet, temizle',
      cancelLabel: 'Vazgeç',
      iconClass: 'fas fa-question-circle',
    });
    if (!onay) return;
    setFiltre({ ...varsayilanRef });
    setSayfa(1);
  };

  const tipEtiket = tip === 'giden' ? 'giden' : 'gelen';
  const bosMesaj = tip === 'giden'
    ? 'Seçilen aralıkta giden çağrı bulunamadı.'
    : 'Seçilen aralıkta gelen çağrı bulunamadı.';

  if (hideTelefonFiltresi && !sabitTelefon?.trim()) {
    return (
      <div className="empty-state">
        <i className="fas fa-phone-slash" aria-hidden />
        <span>Müşteriye kayıtlı telefon numarası yok.</span>
      </div>
    );
  }

  return (
    <>
      <div className="gelen-cagrilar-toolbar">
        <button
          type="button"
          className={`secondary-btn${filtreAktif ? ' filter-active' : ''}`}
          onClick={() => setFiltreModalAcik(true)}
        >
          <i className="fas fa-filter" aria-hidden /> Filtrele
        </button>
        {filtreAktif ? (
          <button
            type="button"
            className="secondary-btn gelen-filtre-temizle"
            onClick={handleFiltreTemizle}
          >
            <i className="fas fa-times" aria-hidden /> Filtreyi temizle
          </button>
        ) : null}
        <button
          type="button"
          className="secondary-btn"
          onClick={() => loadList()}
          disabled={loading}
        >
          <i className={`fas fa-sync-alt${loading ? ' fa-spin' : ''}`} aria-hidden /> Yenile
        </button>
      </div>

      <p className="gelen-cagrilar-hint">
        Sayfa başına {limit} kayıt
        {toplam > 0 ? <> · toplam {toplam} birleştirilmiş {tipEtiket} görüşme</> : null}.
        Detay için satırdaki bilgi ikonuna tıklayın.
      </p>

      {error && (
        <div className="error-state" style={{ marginBottom: '16px' }}>
          <i className="fas fa-exclamation-circle" aria-hidden />
          <span>{error}</span>
        </div>
      )}

      {loading ? (
        <div className="loading-state">
          <i className="fas fa-spinner fa-spin" aria-hidden />
          <span>Yükleniyor...</span>
        </div>
      ) : list.length === 0 ? (
        <div className="empty-state">
          <i className="fas fa-phone-slash" aria-hidden />
          <span>{bosMesaj}</span>
        </div>
      ) : (
        <>
          <div className="table-container gelen-cagrilar-wrap">
            <table className="admins-table gelen-cagrilar-table">
              <thead>
                <tr>
                  <th>Tarih</th>
                  <th>Arayan</th>
                  {!hideMusteriKolonu ? <th>Müşteri</th> : null}
                  <th className="gc-col-aranan">Aranan</th>
                  <th>Süre</th>
                  <th>Durum</th>
                  <th className="gc-col-mesai">Mesai</th>
                  <th className="ses-kaydi-sutunu">Ses kaydı</th>
                  <th className="detay-sutunu">Detay</th>
                </tr>
              </thead>
              <tbody>
                {list.map((c, idx) => (
                  <tr key={c.commonId ? `${c.commonId}-${c.telefon}` : (c.uniqueid ? `${c.uniqueid}-${idx}` : idx)}>
                    <td className="cell-nowrap">{formatTarih(c.tarih) || c.tarihMetin || '—'}</td>
                    <td>{c.arayan || '—'}</td>
                    {!hideMusteriKolonu ? (
                      <td className="cell-truncate" title={c.musteriAdi || undefined}>{c.musteriAdi || '—'}</td>
                    ) : null}
                    <td className="cell-truncate gc-col-aranan" title={c.aranan || undefined}>
                      {tip === 'giden' ? (c.telefon || c.aranan || '—') : (c.aranan || '—')}
                    </td>
                    <td className="cell-nowrap">
                      {c.sureMetin || '—'}
                      {c.bacakSayisi > 1 ? (
                        <span className="gelen-cagrilar-bacak" title={`${c.bacakSayisi} santral kaydı birleştirildi`}>
                          ({c.bacakSayisi})
                        </span>
                      ) : null}
                    </td>
                    <td>
                      <span className={`gelen-cagrilar-badge gelen-cagrilar-badge--${Number(c.yon) === cevaplandiYon(tip) ? 'ok' : 'miss'}`}>
                        {c.durum}
                      </span>
                    </td>
                    <td className="gc-col-mesai">
                      <span className={`gelen-cagrilar-badge gelen-cagrilar-badge--${c.mesaiDisi ? 'off' : 'on'}`}>
                        {c.mesaiDisi ? 'Dışı' : 'İçi'}
                      </span>
                    </td>
                    <td className="ses-kaydi-sutunu">
                      {c.sesKaydi ? (
                        <button
                          type="button"
                          className="ses-kaydi-dinle-btn ses-kaydi-dinle-btn--compact"
                          onClick={() => setSesKaydiModal(c)}
                          title="Ses kaydını dinle"
                          aria-label="Ses kaydını dinle"
                        >
                          <i className="fas fa-circle-play" aria-hidden />
                        </button>
                      ) : (
                        <span className="ses-kaydi-empty">—</span>
                      )}
                    </td>
                    <td className="detay-sutunu">
                      <button
                        type="button"
                        className="ses-kaydi-info-btn ses-kaydi-info-btn--compact"
                        onClick={() => setDetayModal(c)}
                        title="Çağrı detayı"
                        aria-label="Çağrı detayı"
                      >
                        <i className="fas fa-circle-info" aria-hidden />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <TableListFooter
            totalRecords={toplam}
            currentPage={sayfa}
            totalPages={toplamSayfa || 1}
            onPageChange={setSayfa}
            loading={loading}
          />
        </>
      )}

      {detayModal && (
        <GelenCagriDetayModal
          cagri={detayModal}
          onClose={() => setDetayModal(null)}
        />
      )}

      {sesKaydiModal && (
        <SesKaydiPlayerModal
          kayit={sesKaydiModal}
          onClose={() => setSesKaydiModal(null)}
        />
      )}

      <GelenCagriFilterModal
        isOpen={filtreModalAcik}
        onClose={() => setFiltreModalAcik(false)}
        onApply={handleFiltreUygula}
        initialFilters={filtre}
        bugunStr={bugunStr()}
        telefonGizle={hideTelefonFiltresi}
        tip={tip}
      />
    </>
  );
};

export default MusteriCagriListesi;
